/**
 * Debug bar component that displays user information at the bottom of the screen.
 * Only visible on desktop devices.
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { testDatabaseConnection } from "@/lib/dbConnectionTest";
import { CheckCircle2, XCircle, Loader2, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { User } from "@supabase/supabase-js";
import { useHeadlessMode } from "@/contexts/HeadlessModeContext";
import { useDebugUserLevel } from "@/contexts/DebugUserLevelContext";

/**
 * Gets client IP address from a public API service
 */
const getClientIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || 'Unknown';
  } catch (error) {
    console.error('Error fetching client IP:', error);
    return 'Unknown';
  }
};

/**
 * Gets user role from the database
 * Returns 'user' as default if role cannot be fetched (e.g., RLS restrictions, no role assigned)
 */
const getUserRole = async (userId: string): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .order('role', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Handle errors gracefully - 406 errors are likely RLS/permission issues
    if (error) {
      // Only log non-406 errors to avoid console spam
      // 406 (Not Acceptable) typically indicates RLS policy restrictions
      const is406Error = error.status === 406 || 
                        error.message?.includes('406') || 
                        error.code === 'PGRST116';
      
      if (!is406Error) {
        console.error('Error fetching user role:', error);
      }
      return 'user';
    }

    if (!data) {
      return 'user';
    }

    return data.role || 'user';
  } catch (error) {
    // Suppress 406-related errors in catch block
    const errorMessage = error instanceof Error ? error.message : String(error);
    const is406Error = errorMessage.includes('406') || 
                      (error as { status?: number })?.status === 406;
    
    if (!is406Error) {
      console.error('Error fetching user role:', error);
    }
    return 'user';
  }
};

/**
 * Debug bar component that displays IP address, user email, UID, role, and database connection status
 */
interface DebugBarProps {
  onOpenAuthDebugger: () => void;
}

export const DebugBar = ({ onOpenAuthDebugger }: DebugBarProps) => {
  const isMobile = useIsMobile();
  const { isHeadlessMode } = useHeadlessMode();
  const { overrideLevel, setOverrideLevel } = useDebugUserLevel();
  const [ipAddress, setIpAddress] = useState<string>('Loading...');
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string>('Loading...');
  const [dbStatus, setDbStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');

  /**
   * User level options for testing
   */
  const userLevelOptions = [
    { value: "null", label: "Actual" },
    { value: "2", label: "User (2)" },
    { value: "4", label: "Paid (4)" },
    { value: "5", label: "Corporate (5)" },
    { value: "6", label: "Admin (6)" },
    { value: "8", label: "Super Admin (8)" },
  ];

  /**
   * Handles user level selector change
   */
  const handleUserLevelChange = (value: string) => {
    if (value === "null") {
      setOverrideLevel(null);
    } else {
      setOverrideLevel(parseInt(value, 10));
    }
  };

  /**
   * Effect: Fetch IP address on mount
   */
  useEffect(() => {
    getClientIP().then(setIpAddress);
  }, []);

  /**
   * Effect: Check database connection status
   */
  useEffect(() => {
    const checkDatabaseConnection = async () => {
      setDbStatus('checking');
      const result = await testDatabaseConnection();
      setDbStatus(result.success ? 'connected' : 'disconnected');
    };

    // Check immediately on mount
    checkDatabaseConnection();

    // Check every 30 seconds
    const interval = setInterval(checkDatabaseConnection, 30000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Effect: Fetch user info and role on mount and auth state changes
   */
  useEffect(() => {
    const fetchUserInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        const role = await getUserRole(user.id);
        setUserRole(role);
      } else {
        setUserRole('Not logged in');
      }
    };

    fetchUserInfo();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const role = await getUserRole(session.user.id);
        setUserRole(role);
      } else {
        setUserRole('Not logged in');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Don't render on mobile devices or in headless mode
  if (isMobile || isHeadlessMode) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 bg-muted border-t border-border px-4 py-2 transition-all duration-300 ${
      isHeadlessMode ? 'opacity-0 translate-y-full pointer-events-none' : 'opacity-100 translate-y-0'
    }`}>
      <div className="flex items-center justify-between text-xs text-muted-foreground max-w-full overflow-x-auto">
        <div className="flex items-center gap-6 flex-1 min-w-0">
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold">IP:</span>
            <span className="font-mono">{ipAddress}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold">Email:</span>
            <span className="font-mono truncate max-w-[200px]">{user?.email || 'Not logged in'}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold">UID:</span>
            <span className="font-mono truncate max-w-[200px]">{user?.id || 'N/A'}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold">Role:</span>
            <span className="font-mono">{userRole}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold">DB:</span>
            {dbStatus === 'checking' && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}
            {dbStatus === 'connected' && (
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                <span className="text-green-500 font-mono">Connected</span>
              </div>
            )}
            {dbStatus === 'disconnected' && (
              <div className="flex items-center gap-1">
                <XCircle className="h-3 w-3 text-red-500" />
                <span className="text-red-500 font-mono">Disconnected</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold">Test Level:</span>
            <Select
              value={overrideLevel !== null ? overrideLevel.toString() : "null"}
              onValueChange={handleUserLevelChange}
            >
              <SelectTrigger className="h-6 w-[140px] text-xs px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {userLevelOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-2 text-xs hover:bg-background" 
            onClick={onOpenAuthDebugger}
          >
            <Bug className="h-3 w-3 mr-1" />
            Auth Debug
          </Button>
        </div>
      </div>
    </div>
  );
};

